import React, { useEffect } from 'react';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Col } from '@cvent/carina/components/Col';
import { Row } from '@cvent/carina/components/Row';
import { useTranslate } from 'nucleus-text';
import useBreakpoints from '@hooks/useBreakpoints';
import useTheme from '@cvent/blocks/hooks/useTheme';
import AgendaItemCardName from '@components/agendaCard/AgendaItemCardName';
import SessionTime from '@components/agendaCard/SessionTime';
import Link from 'next/link';
import { LoadingSpinner } from '@cvent/carina/components/LoadingSpinner';
import ScrollViewWithBars from '@cvent/carina/components/ScrollViewWithBars/ScrollViewWithBars';
import DismissButton from '@cvent/carina/components/ScrollViewWithBars/DismissButton';
import { Button } from '@cvent/carina/components/Button';
import { CSSObject } from '@emotion/react';
import { AgendaItem, Session } from '@graphql/gql/graphql';
import { useThemeValues } from '@context/ThemeProvider';
import Icon from '@components/shared/Icon';
import Typography from '@cvent/blocks/components/Typography';
import EmptyScheduleFlyoutSVG from './images/EmptyScheduleFlyoutSVG';
import OnDemandSVG from './images/OnDemandSVG';

interface Props {
  testID: string;
  agendaLoading: boolean;
  eventId: string;
  navHeight?: number;
  onLoadMore: () => void;
  onClose: () => void;
  hasSessionList?: boolean;
  hasMore: boolean;
  endedSchduleFlyoutVisible: boolean;
  isOnDemandContentPresent: boolean;
  isFlyoutVisible: boolean;

  // TODO fill in types
  upcomingAgendaItems: AgendaItem[];
  upcomingSessions: Session[];
}

const useStyles = (navHeight?: number): Record<string, CSSObject> => {
  const theme = useTheme();
  const { depth } = theme;
  const { xOffset, yOffset, blurRadius, spreadRadius, color } = depth.boxShadow.hard;
  const { mediaIsMobile, heightWindow } = useBreakpoints();
  return {
    scheduleContainer: {
      margin: 0,
      backgroundColor: theme.block.base,
      width: 496,
      border: 'none',
      borderRadius: theme.size.border.radius.fiveX,
      boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: heightWindow - navHeight,
      position: 'relative',
      [mediaIsMobile]: {
        width: '100%',
        maxHeight: heightWindow,
        height: heightWindow
      }
    },
    hRule: {
      marginTop: '16px',
      marginBottom: '16px',
      height: 1,
      border: 'none',
      backgroundColor: theme.borderColor.soft,
      justifyContent: 'flex-start'
    },
    speakerStyle: {
      display: 'inline',
      color: theme.font.color.active,
      fontSize: theme.font.base.size.s
    },
    titleStyle: {
      paddingTop: 24,
      paddingRight: 24,
      paddingLeft: 24,
      marginTop: 0,
      marginBottom: '24px',
      color: theme.font.color.base,
      [mediaIsMobile]: {
        paddingTop: 16,
        paddingRight: 20,
        paddingLeft: 16
      }
    },
    titleFontSize: {
      fontSize: theme.font.base.size.h4
    },
    agendaItemContainer: {
      width: '100%',
      paddingRight: 24,
      paddingLeft: 24,
      [mediaIsMobile]: {
        paddingRight: 20,
        paddingLeft: 16
      }
    },
    locationStyle: {
      color: theme.font.color.soft,
      fontSize: theme.font.base.size[3],
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'left'
    },
    emptyStateText: {
      fontSize: theme.font.base.size.h4,
      color: theme.font.color.base,
      paddingRight: 24,
      paddingLeft: 24,
      [mediaIsMobile]: {
        paddingRight: 20,
        paddingLeft: 16
      }
    },
    loadingSpinnerContainer: {
      borderRadius: theme.size.border.radius.fiveX,
      boxShadow: `${xOffset} ${yOffset} ${blurRadius} ${spreadRadius} ${color}`
    },
    emptyStateTitleStyle: {
      marginBottom: 8,
      [mediaIsMobile]: {
        marginRight: 50
      }
    },
    emptyStateSubtitle: {
      paddingBottom: 8
    },
    flyoutExtraText: {
      color: theme.font.color.soft,
      fontSize: theme.font.base.size[3],
      textAlign: 'center'
    },
    close: {
      backgroundColor: 'transparent',
      color: theme.font.color.base,
      border: 'none'
    },
    closeContainer: {
      paddingRight: 16,
      paddingTop: 16
    },
    imageContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%'
    },
    locationText: {
      lineHeight: '130%',
      paddingLeft: '4px'
    },
    locationHRule: {
      marginTop: '14px'
    }
  };
};

function MyScheduleFlyoutContent({
  testID,
  upcomingAgendaItems,
  agendaLoading,
  eventId,
  navHeight = 0,
  onLoadMore,
  onClose,
  hasSessionList = true,
  hasMore,
  upcomingSessions = [],
  endedSchduleFlyoutVisible,
  isOnDemandContentPresent,
  isFlyoutVisible
}: Props): JSX.Element {
  const { translate } = useTranslate();
  const { hideIllustrations } = useThemeValues();
  const styles = useStyles(navHeight);
  const { isMobile } = useBreakpoints();

  const isEmpty = upcomingAgendaItems?.length === 0;
  const firstThree = upcomingAgendaItems?.slice(0, 3);
  const upcomingSessionsExist = upcomingSessions?.length > 0;

  const contentBody = () => {
    if (!isEmpty) {
      return firstThree?.map(item => (
        <Col padding={0} key={item.id} role="listitem">
          <div css={styles.agendaItemContainer} {...injectTestId('schedule-popover-agenda-item')}>
            <AgendaItemCardName
              id={item.id}
              eventId={eventId}
              name={item.name}
              type={item.type}
              smallerFontandSpacing
              onClose={onClose}
              titleStyleAs="p"
              titleAs="div"
              titleStyle={styles.titleFontSize}
            />
            <SessionTime
              start={item.start}
              end={item.end}
              showDate
              smallerFont
              showMultiDay
              noBreak
              showShortNumericDate
            />
            {item.location && (
              <div css={styles.locationStyle} {...injectTestId('schedule-popover-agenda-item-location')}>
                <div>
                  <Icon name="map-pin" size="xs" aria-hidden />
                </div>
                <div css={styles.locationText}>{item.location.name}</div>
              </div>
            )}
            <hr css={[styles.hRule, item.location && styles.locationHRule]} aria-hidden />
          </div>
        </Col>
      ));
    }
    if (endedSchduleFlyoutVisible && isOnDemandContentPresent && !hideIllustrations) {
      return (
        <div css={styles.imageContainer}>
          <OnDemandSVG testID="on-demand-video-image" />
        </div>
      );
    }
    return (
      !hideIllustrations && (
        <div css={styles.imageContainer}>
          <EmptyScheduleFlyoutSVG testID="calendar-image" />
        </div>
      )
    );
  };

  const flyoutSubtitle = () => {
    if (endedSchduleFlyoutVisible) {
      return translate('MyEndedSchedule-Flyout-EndedEvent-Title');
    }
    return isEmpty ? translate('MySchedule-Flyout-Empty-State-Header') : translate('MySchedule-Flyout-Header');
  };

  const flyoutContent = endedSchduleFlyoutVisible ? (
    <>
      <Row>{contentBody()}</Row>
      <Row>
        <Col flex={{ alignItems: 'center', flexDirection: 'column', display: 'flex' }}>
          <Typography as="h3" styledAs="h4" css={styles.emptyStateSubtitle}>
            {flyoutSubtitle()}
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col padding={{ start: 24, end: 24 }}>
          <div css={styles.flyoutExtraText}>
            {isOnDemandContentPresent
              ? translate('MyEndedSchedule-Flyout-With-OnDemand-Body')
              : translate('MyEndedSchedule-Flyout-Without-OnDemand-Body')}
          </div>
        </Col>
      </Row>
      <Row
        justifyContent={isOnDemandContentPresent ? 'flex-start' : 'center'}
        margin={{ top: 24, bottom: 24, end: 12, start: 12 }}
      >
        {isOnDemandContentPresent ? (
          <Col>
            <div css={{ display: 'flex', justifyContent: 'center !important', width: 'fill' }}>
              <Link href={`/events/${eventId}/ondemand`} passHref css={{ padding: 0 }} legacyBehavior>
                <Button
                  text={translate('MyEndedSchedule-Flyout-VideoLibrary-Button')}
                  testID="view-ondemand-page-link"
                  appearance="filled"
                  onClick={onClose}
                />
              </Link>
              <div css={{ width: '16px' }} />
              <Link href={`/events/${eventId}/schedule`} passHref legacyBehavior>
                <Button
                  text={translate('MySchedule-Flyout-Button')}
                  testID="view-my-schedule-link"
                  appearance="ghost"
                  onClick={onClose}
                />
              </Link>
            </div>
          </Col>
        ) : (
          <Col width="content" offset={isEmpty ? 10 : 0} flex={{ alignItems: 'center', display: 'flex' }}>
            <Link href={`/events/${eventId}/schedule`} passHref legacyBehavior>
              <Button
                text={translate('MySchedule-Flyout-Button')}
                testID="view-my-schedule-link"
                element="a"
                onClick={() => onClose()}
              />
            </Link>
          </Col>
        )}
      </Row>
    </>
  ) : (
    <>
      <Row role="list">{contentBody()}</Row>
      {upcomingSessionsExist && isEmpty && hasSessionList ? (
        <>
          <Row>
            <Col flex={{ alignItems: 'center', flexDirection: 'column', display: 'flex' }}>
              <Typography as="h3" styledAs="h4" css={styles.emptyStateSubtitle}>
                {flyoutSubtitle()}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col padding={{ start: 24, end: 24 }}>
              <div css={styles.flyoutExtraText}>{translate('MySchedule-Flyout-Empty-State-Body')}</div>
            </Col>
          </Row>
        </>
      ) : null}
      <Row justifyContent="center" margin={{ top: isEmpty ? 24 : 8, bottom: 24, end: 12, start: 12 }}>
        {upcomingSessionsExist && isEmpty && hasSessionList ? (
          <div css={{ display: 'flex', justifyContent: 'center !important', width: 'fill' }}>
            <Link href={`/events/${eventId}/sessions`} passHref legacyBehavior>
              <Button
                text={translate('MySchedule-Flyout-AllSessions-Button')}
                testID="flyout-schedule-fill-my-time-button"
                appearance="filled"
                element="a"
                onClick={onClose}
              />
            </Link>
            <div css={{ width: '16px' }} />
            <Link href={`/events/${eventId}/schedule`} passHref legacyBehavior>
              <Button
                text={translate('MySchedule-Flyout-Button')}
                testID="view-my-schedule-link"
                appearance="ghost"
                onClick={onClose}
              />
            </Link>
          </div>
        ) : (
          <Col width="content" offset={isEmpty ? 10 : 0} flex={{ alignItems: 'center', display: 'flex' }}>
            <Link href={`/events/${eventId}/schedule`} passHref legacyBehavior>
              <Button
                text={translate('MySchedule-Flyout-Button')}
                testID="view-my-schedule-link"
                element="a"
                onClick={() => onClose()}
              />
            </Link>
          </Col>
        )}
      </Row>
    </>
  );

  useEffect(() => {
    if (firstThree?.length < 3 && hasMore && !agendaLoading) {
      onLoadMore();
    }
  }, [onLoadMore, firstThree, hasMore, agendaLoading]);

  return (
    <Row role="list">
      {agendaLoading ? (
        <Col minWidth={!isMobile ? 480 : null} padding={{ start: 0, end: 0 }} css={styles.loadingSpinnerContainer}>
          <LoadingSpinner testID="schedule-popover" size="m" />
        </Col>
      ) : (
        isFlyoutVisible && (
          <Col padding={{ start: 0, end: 0 }} role="listitem">
            <div css={styles.scheduleContainer} {...injectTestId(`${testID}`)}>
              <ScrollViewWithBars
                header={
                  <Row>
                    <Col width={10 / 12} padding={0}>
                      <Typography
                        as="h2"
                        styledAs="h3"
                        css={{ ...styles.titleStyle, ...(isEmpty && styles.emptyStateTitleStyle) }}
                      >
                        {translate('MySchedule-Flyout-Title')}
                      </Typography>
                    </Col>
                    <Col width={2 / 12}>
                      {isMobile && (
                        <div css={styles.closeContainer}>
                          <DismissButton
                            type="button"
                            css={styles.close}
                            onClick={() => onClose()}
                            aria-label={translate('MySchedule-Flyout-Mobile-Close-Label')}
                            {...injectTestId('schedule-flyout-close')}
                          />
                        </div>
                      )}
                    </Col>
                  </Row>
                }
              >
                {flyoutContent}
              </ScrollViewWithBars>
            </div>
          </Col>
        )
      )}
    </Row>
  );
}

export default MyScheduleFlyoutContent;
